import React, { useContext, useMemo, useRef } from "react";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import type { StylesConfig } from "react-select";

import {
	faLaptop,
	faQuestionCircle,
	faRightToBracket,
	faVideo,
	faWaveformLines,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AnimatePresence, motion } from "framer-motion";

import { isDesktopApp, isMobile, isNative } from "~/lib/utils";
import MeetingPhaseContext, {
	type RecordingMediaType,
	type RecordingMode,
	someConferenceCallSupportsRecording,
} from "~/stream/MeetingPhaseContext";
import { CallIcon, getCallName } from "~/stream/NoteHeader/JoinConferenceButton";
import { useAutoRecordReason } from "~/stream/StreamSettings/Automations/StreamAutoRecordAutomationConfig";
import { Tooltip as TooltipComponent, type TooltipProps } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Selector from "~/ui/selectors/Selector";
import { condensedStyles } from "~/ui/selectors/SelectorUtils";

import { DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG, WEB_BOTLESS_RECORDING_FEATURE_FLAG } from "../featureFlag";
import { type ConferenceCallOption, RecordingOptionsContext } from "./PreMeetingControls";
import { ToggleGroupItem, ToggleGroupRoot } from "./ToggleGroup";

import type { OptionsMenu_AutoRecordMenu_AutoRecordReason as AutoRecordReason } from "~/__generated__/OptionsMenu_AutoRecordMenu_AutoRecordReason.graphql";
import type { OptionsMenu_AutoRecordMenu_refetchableAutoRecordReason$key } from "~/__generated__/OptionsMenu_AutoRecordMenu_refetchableAutoRecordReason.graphql";
import type { OptionsMenu_note$data as Note, OptionsMenu_note$key } from "~/__generated__/OptionsMenu_note.graphql";
import type { OptionsMenu_viewer$key } from "~/__generated__/OptionsMenu_viewer.graphql";

import styles from "./OptionsMenu.module.css";

type ConferenceCall = NonNullable<Note["event"]>["conferenceCalls"][number];

const SELECTOR_STYLES: StylesConfig<ConferenceCallOption> = {
	control: (base, state) => ({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		...(condensedStyles.control ? condensedStyles.control(base, state as any) : base),
		borderRadius: 8,
		backgroundColor: "transparent",
		cursor: "pointer",
		":hover": { "@media (hover: hover)": { backgroundColor: "var(--secondaryButtonHoverColor)" } },
	}),
	valueContainer: base => ({
		...base,
		fontWeight: "normal",
		paddingRight: 3,
	}),
	dropdownIndicator: base => ({
		...base,
		color: "var(--secondaryTextColor)",
		paddingLeft: 3,
	}),
};

function onToggleGroupChange<T extends string>(setValue: (value: T) => void) {
	return (value: T) => {
		if (!value) return;
		setValue(value);
	};
}

const viewerFragment = graphql`
	fragment OptionsMenu_viewer on Viewer {
		...StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer
	}
`;

const noteFragment = graphql`
	fragment OptionsMenu_note on Note {
		stream {
			id
			schedulingMeetingAssistantAutomationIsActive
			...OptionsMenu_AutoRecordMenu_refetchableAutoRecordReason
		}
		event {
			mainConferenceCall {
				provider
			}
			conferenceCalls {
				provider
			}
		}
	}
`;

const refetchableAutoRecordReasonFragment = graphql`
	fragment OptionsMenu_AutoRecordMenu_refetchableAutoRecordReason on NoteStream
	@refetchable(queryName: "OptionsMenu_AutoRecordMenu_AutoRecordReason") {
		...StreamAutoRecordAutomationConfig_useAutoRecordReason_stream
	}
`;

type OptionsMenuTooltipProps = React.PropsWithChildren<TooltipProps>;
function Tooltip({ children, content, ...props }: OptionsMenuTooltipProps) {
	return (
		<TooltipComponent
			content={<div style={{ textAlign: "center" }}>{content}</div>}
			maxWidth={150}
			placement="top"
			// Tooltips need `appendTo` set to "parent" so that they take variables from the `.invert-theme` class
			appendTo="parent"
			{...props}
		>
			{children}
		</TooltipComponent>
	);
}

function AutoRecordOptions({
	viewer: viewerKey,
	note: noteKey,
}: {
	viewer: OptionsMenu_viewer$key;
	note: OptionsMenu_note$key;
}) {
	const {
		event: { start: eventStart },
	} = useContext(MeetingPhaseContext);

	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const [autoRecordReasonStream] = useRefetchableFragment<
		AutoRecordReason,
		OptionsMenu_AutoRecordMenu_refetchableAutoRecordReason$key
	>(refetchableAutoRecordReasonFragment, note.stream);
	const autoRecordReason = useAutoRecordReason(autoRecordReasonStream, viewer);
	const autoRecordIsOn = note.stream.schedulingMeetingAssistantAutomationIsActive;

	const formattedTime = Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(eventStart);

	// There has to be at least one conference call when rendering auto-record options
	const mainConferenceCall = note.event?.mainConferenceCall;

	return (
		<>
			<div className={styles.header}>
				<div className={styles.titleWithIcon}>
					<span className={styles.title}>Schedule AI Notes</span>
					<Tooltip content="Learn more">
						{/* TODO: Add help center link */}
						<IconButton icon={faQuestionCircle} className={styles.helpCenterIcon} />
					</Tooltip>
				</div>
				<Tooltip content={autoRecordReason} disabled={!autoRecordIsOn}>
					<div className={styles.subtitle}>
						{autoRecordIsOn ? `Fellow will take AI notes at ${formattedTime}` : autoRecordReason}
					</div>
				</Tooltip>
			</div>
			{autoRecordIsOn && (
				<div className={styles.content}>
					<Tooltip content="Fellow must join the call to Auto-record">
						<div className={styles.contentRow}>
							<CallIcon className={styles.conferenceCallIcon} provider={mainConferenceCall?.provider ?? "UNKNOWN"} />
							<span className={styles.fixedChoiceMessage}>Fellow will join as a bot</span>
						</div>
					</Tooltip>
					<div className={styles.contentRow}>
						<FontAwesomeIcon icon={faVideo} className={styles.contentRowIcon} />
						<span className={styles.fixedChoiceMessage}>Will capture audio and video</span>
					</div>
				</div>
			)}
		</>
	);
}

const appendToGrandparent = (element: Element) => element.parentElement?.parentElement ?? element;

function StartRecordingOptions({ note: noteKey }: { note: OptionsMenu_note$key }) {
	const audioOnlyRecordingEnabled = useFeatureIsOn("audio-only-recording");
	const desktopRecordingEnabled = useFeatureIsOn(DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG);
	const desktopRecordingAvailable = desktopRecordingEnabled && window.desktopRecorder;
	const microphoneRecordingEnabled = useFeatureIsOn(WEB_BOTLESS_RECORDING_FEATURE_FLAG);

	const note = useFragment(noteFragment, noteKey);

	const conferenceCalls = useMemo(
		() => note.event?.conferenceCalls ?? ([] as ConferenceCall[]),
		[note.event?.conferenceCalls],
	);

	const meetingHasConferenceCall = conferenceCalls.length > 0;
	const botRecordingSupported = someConferenceCallSupportsRecording(conferenceCalls);

	const {
		recordingMode,
		recordingMediaType,
		setRecordingMode,
		setRecordingMediaType,
		conferenceCallOptions,
		selectedConferenceCall,
		onChangeConferenceCall,
	} = useContext(RecordingOptionsContext);

	return (
		<>
			<div className={styles.header}>
				<div className={styles.titleWithIcon}>
					<span className={styles.title}>Take AI Notes now</span>
					<Tooltip content="Learn more">
						{/* TODO: Add help center link */}
						<IconButton icon={faQuestionCircle} className={styles.helpCenterIcon} />
					</Tooltip>
				</div>
			</div>
			<div className={styles.content}>
				{(desktopRecordingEnabled || microphoneRecordingEnabled) && (desktopRecordingAvailable || !isDesktopApp) ? (
					<div className={styles.contentRow}>
						<span className={styles.contentRow}>
							{recordingMode === "BOT" && selectedConferenceCall ? (
								<CallIcon className={styles.conferenceCallIcon} provider={selectedConferenceCall.value} />
							) : (
								<FontAwesomeIcon icon={faLaptop} className={styles.contentRowIcon} />
							)}
							<span>Record as…</span>
						</span>
						<ToggleGroupRoot
							type="single"
							value={recordingMode}
							onValueChange={onToggleGroupChange<RecordingMode>(setRecordingMode)}
						>
							<Tooltip
								offset={[0, 15]}
								content={
									!meetingHasConferenceCall || !selectedConferenceCall
										? "This requires a conference link on the meeting"
										: !botRecordingSupported
											? "This call does not support bot recording"
											: `Fellow will join the ${getCallName(selectedConferenceCall.value)} call as a participant.`
								}
								// The appended to parent, the tooltip is confined to its width. Since the parent is the ToggleGroupRoot,
								// that width is pretty small. Appending to the "grand" parent solves this.
								appendTo={appendToGrandparent}
							>
								{/* Span is needed to keep the tooltip enabled when the ToggleGroupItem is disabled */}
								<span>
									<ToggleGroupItem value="BOT" disabled={!meetingHasConferenceCall || !botRecordingSupported}>
										Bot
									</ToggleGroupItem>
								</span>
							</Tooltip>
							<Tooltip
								offset={[0, 15]}
								content={
									!microphoneRecordingEnabled && !isDesktopApp
										? "The desktop app enables Fellow to record meetings without a bot."
										: // eslint-disable-next-line max-len
											`Fellow will record from your ${isMobile || isNative ? "phone" : "computer"}, no bot will join.`
								}
								appendTo={appendToGrandparent}
							>
								<span>
									<ToggleGroupItem value="BOTLESS" disabled={!microphoneRecordingEnabled && !isDesktopApp}>
										Botless
									</ToggleGroupItem>
								</span>
							</Tooltip>
						</ToggleGroupRoot>
					</div>
				) : (
					<div className={styles.contentRow}>
						<CallIcon className={styles.conferenceCallIcon} provider={selectedConferenceCall?.value ?? "UNKNOWN"} />
						<span className={styles.fixedChoiceMessage}>Fellow will join as a bot</span>
					</div>
				)}

				{recordingMode === "BOT" && conferenceCallOptions.length > 1 && selectedConferenceCall && (
					<div className={styles.contentRow}>
						<span className={styles.contentRow}>
							<FontAwesomeIcon icon={faRightToBracket} className={styles.contentRowIcon} />
							<span>Join…</span>
						</span>
						<Selector
							condensed
							styles={SELECTOR_STYLES}
							options={conferenceCallOptions}
							value={selectedConferenceCall}
							onChange={onChangeConferenceCall}
						/>
					</div>
				)}
				{audioOnlyRecordingEnabled &&
					(recordingMode === "BOT" && meetingHasConferenceCall ? (
						<div className={styles.contentRow}>
							<span className={styles.contentRow}>
								<FontAwesomeIcon
									icon={recordingMediaType === "AUDIO" ? faWaveformLines : faVideo}
									className={styles.contentRowIcon}
								/>
								<span>Capture…</span>
							</span>
							<ToggleGroupRoot
								type="single"
								value={recordingMediaType}
								onValueChange={onToggleGroupChange<RecordingMediaType>(setRecordingMediaType)}
							>
								<ToggleGroupItem value="AUDIO">Audio</ToggleGroupItem>
								<ToggleGroupItem value="VIDEO">Video</ToggleGroupItem>
							</ToggleGroupRoot>
						</div>
					) : (
						<Tooltip content="Fellow must join the call to record video" offset={[0, 0]}>
							<div className={styles.contentRow}>
								<FontAwesomeIcon icon={faWaveformLines} className={styles.contentRowIcon} />
								<span className={styles.fixedChoiceMessage}>Will capture audio locally</span>
							</div>
						</Tooltip>
					))}
			</div>
		</>
	);
}

export default function OptionsMenu({
	viewer,
	note,
	menuType,
	targetPosition,
	onMouseEnterMenu,
	onMouseLeaveMenu,
}: {
	viewer: OptionsMenu_viewer$key;
	note: OptionsMenu_note$key;
	menuType: "autoRecord" | "startRecording";
	targetPosition: number | null;
	onMouseEnterMenu: () => void;
	onMouseLeaveMenu: () => void;
}) {
	const menuOriginRef = useRef<HTMLDivElement>(null);

	const animatedXPosition = useMemo(() => {
		if (!menuOriginRef.current || !targetPosition) return 0;
		return targetPosition - menuOriginRef.current?.getBoundingClientRect().x;
	}, [targetPosition]);

	return (
		<>
			<div ref={menuOriginRef} className={styles.positionOrigin} />
			<AnimatePresence>
				{targetPosition !== null && (
					<motion.div
						className={styles.container}
						initial={{ opacity: 0, x: animatedXPosition }}
						animate={{ opacity: 1, x: animatedXPosition }}
						exit={{ opacity: 0 }}
						transition={{
							opacity: { duration: 0.2, ease: "easeOut" },
							x: { duration: 0.15, ease: "easeOut" },
						}}
						onMouseEnter={onMouseEnterMenu}
						onMouseLeave={onMouseLeaveMenu}
					>
						{menuType === "autoRecord" ? (
							<AutoRecordOptions viewer={viewer} note={note} />
						) : (
							<StartRecordingOptions note={note} />
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
}
